/**
 * Importing a list of people from a spreadsheet.
 *
 * Accepts both .csv and Excel .xlsx/.xls.
 *
 * CSV is parsed here by hand — the format we need is small, and a parser costs
 * nothing on a page anonymous visitors load. Excel files are ZIP archives and
 * genuinely need a library, so SheetJS is pulled in DYNAMICALLY, only once
 * somebody actually picks an .xlsx. A visitor who never imports one never
 * downloads it.
 *
 * Excel sheets are converted to CSV and run through the same parser, so header
 * matching, validation and row numbering behave identically whichever format
 * arrives.
 *
 * The import is deliberately forgiving about headers — an HR export will say
 * "Full Name" or "Name" or "Employee Name", and refusing it over a header
 * spelling would make the feature useless. It is strict about the DATA though:
 * every row that cannot be used is reported with its row number, so nobody
 * discovers a missing surname at the payment screen.
 */

import { splitFullName } from './nameParts';

export interface ImportedSubject {
    fullName: string;
    employeeEmail: string;
    employeePhone: string;
    employeeGender?: string;
    investigationCategory?: string;
}

export interface ImportResult {
    subjects: ImportedSubject[];
    /** One line per unusable row, already worded for display. */
    errors: string[];
}

/** Header spellings we accept, normalised, mapped to the field they fill. */
const HEADER_ALIASES: Record<string, keyof ImportedSubject> = {
    name: 'fullName',
    fullname: 'fullName',
    employeename: 'fullName',
    candidatename: 'fullName',
    staffname: 'fullName',
    email: 'employeeEmail',
    emailaddress: 'employeeEmail',
    mail: 'employeeEmail',
    phone: 'employeePhone',
    phonenumber: 'employeePhone',
    mobile: 'employeePhone',
    mobilenumber: 'employeePhone',
    telephone: 'employeePhone',
    gender: 'employeeGender',
    sex: 'employeeGender',
    category: 'investigationCategory',
    type: 'investigationCategory',
    checktype: 'investigationCategory',
};

const normaliseHeader = (h: string) => h.toLowerCase().replace(/[^a-z]/g, '');

/**
 * Split CSV text into rows of cells.
 *
 * Handles quoted cells containing commas, escaped double quotes ("") and
 * newlines inside quotes — all of which appear the moment someone's data has a
 * comma in it, which is exactly when a naive split would corrupt the import
 * silently rather than loudly.
 */
export function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let inQuotes = false;

    // Strip a UTF-8 BOM, which Excel writes and which would otherwise become
    // part of the first header name.
    const input = text.replace(/^\uFEFF/, '');

    for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (inQuotes) {
            if (char === '"') {
                if (input[i + 1] === '"') {
                    cell += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                cell += char;
            }
            continue;
        }

        if (char === '"') {
            inQuotes = true;
        } else if (char === ',') {
            row.push(cell);
            cell = '';
        } else if (char === '\n' || char === '\r') {
            // Treat CRLF as one break rather than an extra blank row.
            if (char === '\r' && input[i + 1] === '\n') i++;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        } else {
            cell += char;
        }
    }

    // Whatever is still buffered is the last row, unless the file ended on a
    // newline and there is nothing pending.
    if (cell !== '' || row.length > 0) {
        row.push(cell);
        rows.push(row);
    }

    // Blank rows are KEPT. They are skipped when mapping, but dropping them
    // here would shift every later row number, and an error that says "row 5"
    // has to mean row 5 in the file the person is looking at.
    return rows;
}

/** Normalise a Nigerian number to the +234XXXXXXXXXX the API expects. */
function normalisePhone(raw: string): string {
    const digits = (raw ?? '').replace(/[^\d+]/g, '').replace(/^\+/, '');
    if (!digits) return '';
    // 0801… → +234801…, 234801… → +234801…, 801… → +234801…
    if (digits.startsWith('234')) return `+${digits}`;
    if (digits.startsWith('0')) return `+234${digits.slice(1)}`;
    return `+234${digits}`;
}

function normaliseGender(raw: string): string | undefined {
    const g = (raw ?? '').trim().toLowerCase();
    if (g.startsWith('m')) return 'male';
    if (g.startsWith('f')) return 'female';
    return undefined;
}

function normaliseCategory(raw: string): string | undefined {
    const c = (raw ?? '').trim().toLowerCase();
    if (!c) return undefined;
    // "Domestic staff", "domestic", "house help" all mean the same thing here.
    if (c.includes('domestic') || c.includes('house')) return 'domestic_staff';
    if (c.includes('employ') || c.includes('corporate') || c.includes('staff')) return 'employment';
    return undefined;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Turn CSV text into people, reporting every row that cannot be used.
 *
 * A partially usable file still imports: the good rows come back and the bad
 * ones are listed, so someone with one typo in row 7 does not have to redo the
 * whole export.
 */
export function parseSubjectCsv(text: string): ImportResult {
    const rows = parseCsv(text);
    const hasContent = rows.some((r) => r.some((c) => c.trim() !== ''));
    if (!hasContent) {
        return { subjects: [], errors: ['That file is empty.'] };
    }

    const headers = (rows[0] ?? []).map(normaliseHeader);
    const columns = headers.map((h) => HEADER_ALIASES[h]);

    if (!columns.includes('fullName') || !columns.includes('employeeEmail')) {
        return {
            subjects: [],
            errors: [
                'Could not find a name and an email column. The first row should be headings — for example: Full Name, Email, Phone, Gender, Category.',
            ],
        };
    }

    const subjects: ImportedSubject[] = [];
    const errors: string[] = [];

    rows.slice(1).forEach((cells, index) => {
        // +2: skip the header row, and count from 1 like a spreadsheet does.
        const rowNumber = index + 2;
        const record: Partial<ImportedSubject> = {};

        columns.forEach((field, columnIndex) => {
            if (!field) return;
            record[field] = (cells[columnIndex] ?? '').trim();
        });

        const fullName = (record.fullName ?? '').trim();
        const email = (record.employeeEmail ?? '').trim();

        if (!fullName && !email) return; // genuinely blank row

        if (!splitFullName(fullName)) {
            errors.push(`Row ${rowNumber}: "${fullName || 'blank'}" needs a first and last name.`);
            return;
        }
        if (!EMAIL_RE.test(email)) {
            errors.push(`Row ${rowNumber}: "${email || 'blank'}" is not a valid email address.`);
            return;
        }

        const phone = normalisePhone(record.employeePhone ?? '');
        if (!phone) {
            errors.push(`Row ${rowNumber}: ${fullName} has no phone number.`);
            return;
        }

        const gender = normaliseGender(record.employeeGender ?? '');
        const category = normaliseCategory(record.investigationCategory ?? '');

        subjects.push({
            fullName,
            employeeEmail: email,
            employeePhone: phone,
            ...(gender ? { employeeGender: gender } : {}),
            ...(category ? { investigationCategory: category } : {}),
        });
    });

    if (subjects.length === 0 && errors.length === 0) {
        errors.push('That file has headings but no people in it.');
    }

    return { subjects, errors };
}

/** The template we hand out, so the expected columns are never a guess. */
export const SUBJECT_CSV_TEMPLATE = [
    'Full Name,Email,Phone,Gender,Category',
    'Chidi Emeka Okafor,chidi@example.com,08012345678,Male,Employment',
    'Ngozi Adaeze Obi,ngozi@example.com,08087654321,Female,Domestic staff',
].join('\n');

/** Spreadsheet formats the import accepts, for the file picker. */
export const SUBJECT_IMPORT_ACCEPT = '.csv,.xlsx,.xls,text/csv';

function isExcel(file: File): boolean {
    return /\.(xlsx|xls|xlsm)$/i.test(file.name);
}

/**
 * Read a picked file into people, whatever format it is.
 *
 * Excel goes through SheetJS to become CSV, then down the same path as a real
 * CSV — one set of rules for both, and row numbers that still line up with
 * what the person sees in their spreadsheet.
 */
export async function parseSubjectFile(file: File): Promise<ImportResult> {
    if (!isExcel(file)) {
        return parseSubjectCsv(await file.text());
    }

    let XLSX: typeof import('xlsx');
    try {
        // Loaded on demand: several hundred kilobytes that only an Excel
        // import needs to pay for.
        XLSX = await import('xlsx');
    } catch {
        return {
            subjects: [],
            errors: ['Could not load the Excel reader. Save the file as CSV and try again.'],
        };
    }

    try {
        const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = firstSheetName ? workbook.Sheets[firstSheetName] : undefined;
        if (!sheet) {
            return { subjects: [], errors: ['That workbook has no sheets in it.'] };
        }

        // blankrows keeps empty rows so a reported row number still matches the
        // row the person is looking at in Excel.
        return parseSubjectCsv(XLSX.utils.sheet_to_csv(sheet, { blankrows: true }));
    } catch {
        return {
            subjects: [],
            errors: ['That file could not be read as a spreadsheet. Try saving it as CSV.'],
        };
    }
}
